import { AnyAction } from 'redux';
import { Operation } from '@apollo/client';

import config from 'config';
import {
  buildEventDetails,
  buildReduxActionEventDetails,
  buildApolloOperationEventDetails,
  defaultContext,
} from 'metrics/util';

import { AnalyticsClient, AnalyticsClientConfiguration } from './util';

declare global {
  interface Window {
    analytics?: AnalyticsClient;
  }
}

export const getSegmentClient = (_name, client?: AnalyticsClient) => window?.analytics ?? client;

export class SegmentMetrics {
  public name: string;

  public options?: object;

  public configured: AnalyticsClientConfiguration;

  constructor({ name = 'segment', write = config.segment, debug = false, ...options } = {}) {
    this.name = name;
    this.options = options;
    this.configured = {
      traits: null,
      properties: null,
      context: null,
      options: null,
    };

    if (debug) {
      this.analytics.debug(debug);
    }
    if (write) {
      this.analytics.load(write);
    }
  }

  get analytics() {
    return getSegmentClient(this.name);
  }

  configure(configuration = {}) {
    this.configured = {
      ...this.configured,
      ...configuration,
    };
  }

  identify(
    userId: string | null,
    { __options: { context: passedContext = {}, ...options } = {}, ...traits } = {}
  ) {
    return new Promise((resolve) =>
      this.analytics.identify(
        userId,
        {
          ...this.configured.traits,
          ...traits,
        },
        {
          context: {
            ...defaultContext,
            ...this.configured.context,
            ...passedContext,
          },
          ...this.configured.options,
          ...options,
        },
        resolve
      )
    );
  }

  // NOTE-RT: To conform to the `react-metrics` spec, the first argument needs to be an event name, even though we're just gonna be throwing it away
  pageView(
    _eventName?: string,
    { __options: { context: passedContext = {}, ...options } = {}, ...properties } = {}
  ) {
    return new Promise((resolve) =>
      this.analytics.page(
        {
          ...this.configured.properties,
          ...properties,
        },
        {
          context: {
            ...defaultContext,
            ...this.configured.context,
            ...passedContext,
          },
          ...this.configured.options,
          ...options,
        },
        resolve
      )
    );
  }

  reset() {
    return new Promise((resolve) => {
      this.analytics.reset();

      resolve();
    });
  }

  track(
    event: string,
    { __options: { context: passedContext = {}, ...options } = {}, ...properties } = {}
  ) {
    return new Promise((resolve) =>
      this.analytics.track(
        event,
        buildEventDetails({
          ...this.configured.properties,
          ...properties,
        }),
        {
          context: {
            ...defaultContext,
            ...this.configured.context,
            ...passedContext,
          },
          ...this.configured.options,
          ...options,
        },
        resolve
      )
    );
  }

  trackReduxAction(action: AnyAction, supplementaryDetails = {}) {
    return this.track('redux-action', buildReduxActionEventDetails(action, supplementaryDetails));
  }

  trackApolloOperation(operation: Operation, supplementaryDetails = {}) {
    return this.track(
      'apollo-operation',
      buildApolloOperationEventDetails(operation, supplementaryDetails)
    );
  }
}

export default SegmentMetrics;
